import React from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CurrentPlanOverview = ({ subscription, onUpgrade }) => {
  const planName = subscription?.subscription_plans?.name || 'Free';
  const tier = subscription?.tier || 'free';
  const maxAgents = subscription?.subscription_plans?.max_agents || 2;
  const price = subscription?.subscription_plans?.price || 0;
  const annualPrice = subscription?.subscription_plans?.annual_price || 0;
  const billingCycle = subscription?.billing_cycle || 'monthly';
  const features = subscription?.subscription_plans?.features || [];
  const status = subscription?.status || 'active';
  const currentPeriodEnd = subscription?.current_period_end;
  const cancelAtPeriodEnd = subscription?.cancel_at_period_end;
  const prorationCredit = subscription?.proration_credit || 0;
  const prorationCharge = subscription?.proration_charge || 0;
  const retentionOfferApplied = subscription?.retention_offer_applied || false;
  const retentionDiscount = subscription?.retention_offer_discount || 0;
  const retentionExpires = subscription?.retention_offer_expires;
  const pauseEnd = subscription?.pause_end;
  const isFree = tier === 'free';

  const displayPrice = billingCycle === 'annual' ? annualPrice : price;
  const displayPeriod = billingCycle === 'annual' ? '/year' : '/month';
  const discountedPrice = retentionOfferApplied && retentionDiscount > 0
    ? (displayPrice * (1 - retentionDiscount / 100))?.toFixed(2)
    : null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = () => {
    const colors = {
      active: 'text-green-500 bg-green-500/10',
      canceled: 'text-red-500 bg-red-500/10',
      past_due: 'text-orange-500 bg-orange-500/10',
      trialing: 'text-blue-500 bg-blue-500/10',
      paused: 'text-purple-500 bg-purple-500/10'
    };
    return colors?.[status] || colors?.active;
  };

  const getPlanGradient = () => {
    const gradients = {
      free: 'from-gray-500/10 to-gray-600/5',
      draftiq_plus: 'from-blue-500/10 to-blue-600/5',
      enterprise: 'from-purple-500/10 to-purple-600/5'
    };
    return gradients?.[tier] || gradients?.free;
  };

  return (
    <div className="space-y-6">
      {/* Plan Card */}
      <div className={`bg-gradient-to-br ${getPlanGradient()} rounded-xl border border-border p-8`}>
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="CreditCard" size={32} color="var(--color-primary)" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-foreground font-heading">{planName}</h2>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor()}`}>
                  {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
                </span>
                {billingCycle === 'annual' && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-500/10 text-green-500">
                    Annual Billing
                  </span>
                )}
                {retentionOfferApplied && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-500/10 text-green-500">
                    20% Off Applied
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                {discountedPrice ? (
                  <>
                    <span className="text-3xl font-bold text-foreground">${discountedPrice}</span>
                    <span className="text-xl text-muted-foreground line-through">${displayPrice?.toFixed(2)}</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-foreground">${displayPrice?.toFixed(2)}</span>
                )}
                {!isFree && <span className="text-muted-foreground">{displayPeriod}</span>}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Icon name="Users" size={16} />
                  <span>Up to {maxAgents} agents</span>
                </div>
                {currentPeriodEnd && status !== 'paused' && (
                  <div className="flex items-center gap-1.5">
                    <Icon name="Calendar" size={16} />
                    <span>
                      {cancelAtPeriodEnd ? 'Expires' : 'Renews'} {formatDate(currentPeriodEnd)}
                    </span>
                  </div>
                )}
                {status === 'paused' && pauseEnd && (
                  <div className="flex items-center gap-1.5">
                    <Icon name="Calendar" size={16} />
                    <span>Resumes {formatDate(pauseEnd)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {isFree && (
            <Button
              variant="default"
              size="lg"
              onClick={onUpgrade}
              iconName="ArrowRight"
              iconPosition="right"
            >
              Upgrade Plan
            </Button>
          )}
        </div>
      </div>

      {/* Retention Offer Info */}
      {retentionOfferApplied && retentionExpires && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name="Gift" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                Retention Discount Active
              </p>
              <p className="text-sm text-muted-foreground">
                You're saving {retentionDiscount}% on your subscription until {formatDate(retentionExpires)}. After that, your subscription will return to the regular price of ${displayPrice?.toFixed(2)}{displayPeriod}.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Proration Info */}
      {(prorationCredit > 0 || prorationCharge > 0) && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 font-heading flex items-center gap-2">
            <Icon name="Calculator" size={20} className="text-primary" />
            Recent Proration
          </h3>
          <div className="space-y-3">
            {prorationCredit > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Credit from previous plan</span>
                <span className="text-sm font-semibold text-green-500">-${prorationCredit?.toFixed(2)}</span>
              </div>
            )}
            {prorationCharge > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Charge for new plan</span>
                <span className="text-sm font-semibold text-foreground">+${prorationCharge?.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Features & Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Features */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 font-heading flex items-center gap-2">
            <Icon name="CheckCircle" size={20} className="text-primary" />
            Plan Features
          </h3>
          <ul className="space-y-3">
            {features?.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Usage Statistics */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 font-heading flex items-center gap-2">
            <Icon name="BarChart3" size={20} className="text-primary" />
            Usage Statistics
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Active Agents</span>
                <span className="text-sm font-semibold text-foreground">4 / {maxAgents}</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${(4 / maxAgents) * 100}%` }}
                />
              </div>
            </div>
            <div className="pt-4 border-t border-border space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Tasks Completed</span>
                <span className="text-sm font-semibold text-foreground">904</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Active Tasks</span>
                <span className="text-sm font-semibold text-foreground">41</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Avg Efficiency</span>
                <span className="text-sm font-semibold text-foreground">90%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {cancelAtPeriodEnd && (
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                Subscription Ending
              </p>
              <p className="text-sm text-muted-foreground">
                Your subscription will end on {formatDate(currentPeriodEnd)}. You'll be downgraded to the Free plan.
              </p>
            </div>
          </div>
        </div>
      )}

      {status === 'paused' && pauseEnd && (
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name="Pause" size={20} className="text-purple-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                Subscription Paused
              </p>
              <p className="text-sm text-muted-foreground">
                Your subscription is paused and will automatically resume on {formatDate(pauseEnd)}. You can resume earlier from Plan Management.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentPlanOverview;